import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ScheduleEntry, ScheduleErrorState, ToastState } from './types';
import { parseUrlParams, validateParams, generateThemeStyles } from './utils';
import { fetchTruckSchedule } from './services/scheduleService';
import { fetchVendorInfo, VendorInfo } from './services/vendorService';
import ErrorMessage from './components/ErrorMessage';
import LoadingSpinner from './components/LoadingSpinner';
import ScheduleWidget from './components/ScheduleWidget';
import LocationSelector from './components/LocationSelector';
import Toast from './components/Toast';
import './App.css';

function App() {
  const [scheduleData, setScheduleData] = useState<ScheduleEntry[]>([]);
  const [vendorInfo, setVendorInfo] = useState<VendorInfo | null>(null);
  const [selectedTruckId, setSelectedTruckId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scheduleError, setScheduleError] = useState<ScheduleErrorState | null>(null);
  const [showLocationSelector, setShowLocationSelector] = useState(false);
  const [toast, setToast] = useState<ToastState>({
    isVisible: false,
    message: '',
    type: 'info'
  });

  const hasInitialized = useRef(false);

  // Generate theme styles from URL parameters
  const params = parseUrlParams();
  const themeStyles = generateThemeStyles(params.primaryColor, params.theme);

  const showToast = useCallback((message: string, type: 'error' | 'success' | 'info') => {
    setToast({
      isVisible: true,
      message,
      type
    });
  }, []);

  const hideToast = useCallback(() => {
    setToast(prev => ({ ...prev, isVisible: false }));
  }, []);

  const fetchScheduleForTruck = useCallback(async (truckId: string, vendorId: string, environment: string) => {
    try {
      setLoading(true);
      setScheduleError(null); // Clear any previous schedule errors
      const data = await fetchTruckSchedule(truckId, vendorId, environment);
      setScheduleData(data);
      setShowLocationSelector(false);
      setLoading(false);
    } catch (err) {
      console.error('Error loading schedule:', err);
      setLoading(false);
      
      // Show immediate toast notification
      showToast('Schedule could not be retrieved. Please try again.', 'error');
      
      // Create schedule error state with retry functionality
      setScheduleError({
        hasError: true,
        errorMessage: 'Schedule could not be retrieved',
        onRetry: () => {
          // Use a timeout to avoid immediate recursion
          setTimeout(() => {
            setScheduleError(null);
            fetchScheduleForTruck(truckId, vendorId, environment);
          }, 100);
        }
      });
    }
  }, [showToast]);

  useEffect(() => {
    // Prevent double execution in StrictMode and development
    if (hasInitialized.current) {
      console.log('useEffect skipped - already initialized');
      return;
    }
    hasInitialized.current = true;
    console.log('useEffect running - initializing widget');

    const fetchScheduleForTruckLocal = async (truckId: string, vendorId: string, environment: string) => {
      try {
        setLoading(true);
        setScheduleError(null); // Clear any previous schedule errors
        const data = await fetchTruckSchedule(truckId, vendorId, environment);
        setScheduleData(data);
        setShowLocationSelector(false);
        setLoading(false);
      } catch (err) {
        console.error('Error loading schedule:', err);
        setLoading(false);
        
        // Show immediate toast notification
        showToast('Schedule could not be retrieved. Please try again.', 'error');
        
        // Create schedule error state with retry functionality
        setScheduleError({
          hasError: true,
          errorMessage: 'Schedule could not be retrieved',
          onRetry: () => {
            // Use a timeout to avoid immediate recursion
            setTimeout(() => {
              setScheduleError(null);
              fetchScheduleForTruck(truckId, vendorId, environment);
            }, 100);
          }
        });
      }
    };

    const initializeWidget = async () => {
      try {
        // Parse URL parameters
        const params = parseUrlParams();
        
        // Validate required vendorId parameter
        if (!validateParams(params)) {
          setError('Missing required parameter: vendorId must be provided in the URL.');
          setLoading(false);
          return;
        }

        // Always fetch vendor info first
        const vendorData = await fetchVendorInfo(params.vendorId!, params.environment);
        console.log('Vendor data received in App:', vendorData);
        console.log('Truck list count:', vendorData.truckList?.length || 0);
        
        setVendorInfo(vendorData);

        // Check number of trucks returned
        if (vendorData.truckList.length === 0) {
          setError('No locations available for this vendor.');
          setLoading(false);
          return;
        } else if (vendorData.truckList.length === 1) {
          // Only one truck - automatically fetch schedule
          const singleTruck = vendorData.truckList[0];
          setSelectedTruckId(singleTruck.objectId);
          // Use local function to avoid dependency issues
          await fetchScheduleForTruckLocal(singleTruck.objectId, params.vendorId!, params.environment);
        } else {
          // Multiple trucks - show location selector
          setShowLocationSelector(true);
          setLoading(false);
        }
      } catch (err) {
        setError('Failed to load data. Please try again later.');
        setLoading(false);
        console.error('Error initializing widget:', err);
      }
    };

    initializeWidget();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependencies - run only once on mount, protected by ref

  const handleLocationSelect = async (truckId: string) => {
    const params = parseUrlParams();
    setSelectedTruckId(truckId);
    await fetchScheduleForTruck(truckId, params.vendorId!, params.environment);
  };

  const handleBackToLocations = () => {
    setShowLocationSelector(true);
    setScheduleData([]);
    setSelectedTruckId(null);
  };

  // Show loading spinner
  if (loading) {
    return (
      <>
        <LoadingSpinner themeStyles={themeStyles} />
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.isVisible}
          onClose={hideToast}
        />
      </>
    );
  }

  // Show error message
  if (error) {
    return (
      <>
        <ErrorMessage message={error} />
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.isVisible}
          onClose={hideToast}
        />
      </>
    );
  }

  // Show location selector if no truck is selected
  if (showLocationSelector && vendorInfo) {
    const params = parseUrlParams();
    return (
      <>
        <LocationSelector
          truckList={vendorInfo.truckList}
          vendorName={vendorInfo.vendorName}
          environment={params.environment}
          onLocationSelect={handleLocationSelect}
          themeStyles={themeStyles}
        />
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.isVisible}
          onClose={hideToast}
        />
      </>
    );
  }

  // Show schedule widget (including error state)
  if (selectedTruckId) {
    const params = parseUrlParams();
    const hasMultipleLocations = vendorInfo ? vendorInfo.truckList.length > 1 : false;
    
    // Find the selected truck to get its data
    const selectedTruck = vendorInfo?.truckList.find(truck => truck.objectId === selectedTruckId);
    
    return (
      <>
        <ScheduleWidget
          scheduleData={scheduleData}
          truckId={selectedTruckId}
          selectedTruck={selectedTruck}
          environment={params.environment}
          hasMultipleLocations={hasMultipleLocations}
          onBackToLocations={hasMultipleLocations ? handleBackToLocations : undefined}
          scheduleError={scheduleError || undefined}
          themeStyles={themeStyles}
        />
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.isVisible}
          onClose={hideToast}
        />
      </>
    );
  }

  // Fallback state
  return (
    <>
      <LoadingSpinner themeStyles={themeStyles} />
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </>
  );
}

export default App;
